import { FC } from "react";
import type { SpacingKey } from "src/types/emotion";
import { Heading } from "@otrium/atoms";
import { Box } from "@otrium/core";
import { Trans } from "@lingui/macro";
import Link from "next/link";
import { HyperLink } from "src/atoms/HyperLink";
import { StyledCarouselHeader, ViewAllWrapper } from "./CarouselHeader.styled";
import styles from "./CarouselHeader.module.scss";
import { StyledSubtitle } from "src/molecules/ProductCarousel/ProductCarouselInfoText/ProductCarouselInfoText.styled";
import { sanitize } from "isomorphic-dompurify";
import NoSSR from "src/atoms/NoSSR";
import { ChevronTextSmall } from "src/icons/ChevronTextSmall";

const CarouselHeaderTitle: FC<{
  title: string | JSX.Element;
  withChevron?: boolean;
  showViewAllDesktop?: boolean;
  onClick?: () => void;
}> = ({ title, withChevron = false, showViewAllDesktop = false, onClick }) => {
  return (
    <Heading
      as="h3"
      fontFamily="PlusJakartaSans"
      fontSize={2}
      color="tone.black"
      sx={{
        lineHeight: 1.25,
        textTransform: "none",
      }}
      onClick={onClick}
    >
      {typeof title === "string" ? (
        <span
          dangerouslySetInnerHTML={{
            __html: sanitize(title),
          }}
        />
      ) : (
        title
      )}
      <NoSSR>
        {withChevron && (
          <Box
            as="span"
            display={
              showViewAllDesktop
                ? ["inline", "inline", "inline", "none"]
                : ["inline", "inline", "none", "none"]
            }
          >
            <ChevronTextSmall role="presentation" />
          </Box>
        )}
      </NoSSR>
    </Heading>
  );
};

const CarouselHeaderSubtitle: FC<{ subTitle: string }> = ({ subTitle }) => {
  return (
    <StyledSubtitle
      as="h3"
      color="tone.black"
      fontWeight="normal"
      fontSize={["fontSize16", "fontSize16", "fontSize16", "fontSize18"]}
    >
      <span
        dangerouslySetInnerHTML={{
          __html: sanitize(subTitle),
        }}
      />
    </StyledSubtitle>
  );
};

interface CarouselHeaderProps {
  title?: string | JSX.Element;
  subTitle?: string;
  href?: string;
  as?: string;
  prefetch?: boolean;
  viewAllOnClick?: () => void;
  isGated?: boolean;
  showViewAllDesktop?: boolean;
  bottomSpacing?: SpacingKey | SpacingKey[];
}

const CarouselHeader: FC<CarouselHeaderProps> = ({
  title,
  subTitle,
  href,
  as,
  viewAllOnClick,
  isGated,
  showViewAllDesktop = false,
  bottomSpacing = "space16",
}) => {
  return (
    <Box pb={bottomSpacing} className={styles.root}>
      <StyledCarouselHeader
        justifyContent="space-between"
        alignItems="flex-start"
        flexWrap="wrap"
      >
        {title && (
          <div>
            {href ? (
              <Link
                href={href}
                {...(as ? { as } : {})}
                onClick={viewAllOnClick}
              >
                <CarouselHeaderTitle
                  title={title}
                  withChevron
                  showViewAllDesktop={showViewAllDesktop}
                />
              </Link>
            ) : isGated ? (
              <CarouselHeaderTitle
                title={title}
                onClick={viewAllOnClick}
                withChevron={!!viewAllOnClick}
                showViewAllDesktop={showViewAllDesktop}
              />
            ) : (
              <CarouselHeaderTitle title={title} />
            )}
          </div>
        )}
        <NoSSR>
          <ViewAllWrapper
            className={showViewAllDesktop ? "show-view-all-desktop" : ""}
          >
            {href ? (
              <HyperLink
                href={href}
                hasUnderline
                onClick={viewAllOnClick}
                data-testid="carousel-header-link"
              >
                <Trans>View all</Trans>
              </HyperLink>
            ) : viewAllOnClick ? (
              <HyperLink hasUnderline onClick={viewAllOnClick}>
                <Trans>View all</Trans>
              </HyperLink>
            ) : null}
          </ViewAllWrapper>
        </NoSSR>
      </StyledCarouselHeader>
      {subTitle && <CarouselHeaderSubtitle subTitle={subTitle} />}
    </Box>
  );
};

export default CarouselHeader;
